import React, { useEffect, useRef } from 'react'
import PieChart from '../../components/PieChart/PieChart'
import { MacroKcalDistribution } from '@/api/types'
import { roundNumber } from '@/utils'

const PROTEIN = 'Protein'
const FAT = 'Fat'
const CARBOHYDRATES = 'Carbohydrates'

type Props = {
  data: MacroKcalDistribution
  options?: {
    fontSize?: number | string
    labelRadius?: number
    height?: number
    width?: number
  }
}

export function aggregateMacros(data: MacroKcalDistribution[]) {
  const proteinSum = data.reduce((acc, d) => acc + d.proteinKcal, 0)
  const fatSum = data.reduce((acc, d) => acc + d.fatKcal, 0)
  const carbohydratesSum = data.reduce((acc, d) => acc + d.carbohydratesKcal, 0)

  return {
    proteinKcal: proteinSum,
    fatKcal: fatSum,
    carbohydratesKcal: carbohydratesSum,
  }
}

type Data = { name: string; value: number }

export default function MacroKcalPie({ data, options }: Props) {
  const containerRef = useRef<HTMLDivElement>(null)

  const total = data.proteinKcal + data.fatKcal + data.carbohydratesKcal
  const dist: Data[] = [
    { name: PROTEIN, value: roundNumber((data.proteinKcal / total) * 100, 1) },
    { name: FAT, value: roundNumber((data.fatKcal / total) * 100, 1) },
    { name: CARBOHYDRATES, value: roundNumber((data.carbohydratesKcal / total) * 100, 1) },
  ]

  useEffect(() => {
    if (data === undefined) return
    const plot = PieChart<Data>(dist, {
      name: (d) => d.name,
      value: (d) => d.value,
      names: [PROTEIN, FAT, CARBOHYDRATES],
      colors: [
        'var(--color__protein-light)',
        'var(--color__fat-light)',
        'var(--color__carbs-light)',
      ],
      ...options,
    })

    if (containerRef?.current) {
      containerRef.current.append(plot)
    }

    return () => plot.remove()
  }, [data])

  return <div style={{ display: 'flex', justifyContent: 'center' }} ref={containerRef} />
}
